// اجزای مشترک صفحات اطلاعات پایه تدارکات
import React, { useState, useEffect, useRef } from 'react';
import { ui } from '../../components/ListPage';
import WarningDialog from '../../components/WarningDialog';
import { jsonOf, netErr } from '../../utils/http';

export const toFa = n => String(n ?? '').replace(/[0-9]/g, d => '۰۱۲۳۴۵۶۷۸۹'[d]);
export const fmtNum = n => (Number(n || 0) ? Number(n).toLocaleString('fa-IR') : '•');

export const makeApi = base => (path, opts = {}) => fetch(`${base}${path}`, {
  headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${localStorage.getItem('access')}` },
  ...opts,
}).then(jsonOf, netErr);

export const basicApi = makeApi('/api/tadarokat/basic');

/* ── برچسب + ورودی ── */
export function Field({ label, children, flex = 1, width }) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '4px', flex: width ? '0 0 auto' : flex, width, minWidth: 0 }}>
      <label style={ui.mlabel}>{label}</label>
      {children}
    </div>
  );
}

export function TextInput({ value, onChange, readOnly, ltr, type = 'text', maxLength, autoFocus, placeholder }) {
  return (
    <input type={type} value={value ?? ''} onChange={e => onChange && onChange(e.target.value)} readOnly={readOnly}
      maxLength={maxLength} autoFocus={autoFocus} placeholder={placeholder}
      style={{ ...ui.input, direction: ltr ? 'ltr' : 'rtl', textAlign: ltr ? 'left' : 'right', background: readOnly ? '#f5f5f5' : '#fff' }} />
  );
}

/* ── جستجوی انتخابی ── */
export function Lookup({ label, text, endpoint, onPick, api = basicApi, flex = 1, disabled }) {
  const [q, setQ] = useState(text || '');
  const [rows, setRows] = useState([]);
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  useEffect(() => { setQ(text || ''); }, [text]);
  useEffect(() => {
    const h = e => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', h);
    return () => document.removeEventListener('mousedown', h);
  }, []);
  const search = async v => {
    setQ(v);
    const sep = endpoint.includes('?') ? '&' : '?';
    const j = await api(`${endpoint}${sep}q=${encodeURIComponent(v)}`);
    if (j.ok) { setRows(j.rows || []); setOpen(true); }
  };
  return (
    <div ref={ref} style={{ display: 'flex', flexDirection: 'column', gap: '4px', flex, position: 'relative', minWidth: 0 }}>
      {label && <label style={ui.mlabel}>{label}</label>}
      <input value={q} disabled={disabled} onFocus={() => !disabled && search(q && text === q ? '' : q)}
        onChange={e => search(e.target.value)} placeholder="جستجو..."
        style={{ ...ui.input, background: disabled ? '#f5f5f5' : '#fff' }} />
      {!disabled && text && (
        <button type="button" onClick={() => { onPick({ id: 0, display: '', title: '' }); setQ(''); }}
          style={{ position: 'absolute', left: '6px', bottom: '7px', border: 'none', background: 'transparent', cursor: 'pointer', color: '#c62828' }}>✕</button>
      )}
      {open && rows.length > 0 && (
        <div style={{ position: 'absolute', top: '100%', right: 0, left: 0, background: '#fff', border: '1px solid #e0e0e0',
          borderRadius: '8px', boxShadow: '0 8px 24px rgba(0,0,0,0.15)', zIndex: 1700, maxHeight: '240px', overflowY: 'auto' }}>
          {rows.map(r => (
            <div key={r.id} onClick={() => { onPick(r); setOpen(false); }}
              style={{ padding: '7px 10px', cursor: 'pointer', fontSize: '12px', borderBottom: '1px solid #f0f0f0' }}
              onMouseEnter={e => { e.currentTarget.style.background = '#e8f0fe'; }}
              onMouseLeave={e => { e.currentTarget.style.background = '#fff'; }}>{toFa(r.display)}</div>
          ))}
        </div>
      )}
    </div>
  );
}

/* ── جدول ── cols: [{key, label, num, ltr, render}] */
export function DataTable({ cols, rows, selectedId, onSelect, onDouble, onCellContext, rowStyle, empty = 'رکوردی یافت نشد' }) {
  return (
    <>
      <table style={ui.table}>
        <thead style={{ position: 'sticky', top: 0, zIndex: 5 }}>
          <tr>{cols.map(c => <th key={c.key} style={ui.th}>{c.label}</th>)}</tr>
        </thead>
        <tbody>
          {rows.map((r, i) => {
            const sel = selectedId != null && selectedId === r.id;
            const base = (rowStyle && rowStyle(r)) || (i % 2 ? '#fdf6ec' : '#fff');
            const bg = sel ? '#1a2f50' : base;
            return (
              <tr key={r.id ?? i} style={{ background: bg, cursor: 'pointer', color: sel ? '#fff' : '#111' }}
                onClick={() => onSelect && onSelect(r)} onDoubleClick={() => onDouble && onDouble(r)}>
                {cols.map(c => {
                  const v = c.render ? c.render(r) : r[c.key];
                  return (
                    <td key={c.key} onContextMenu={onCellContext ? e => { e.preventDefault(); onSelect && onSelect(r); onCellContext(r, c.key); } : undefined}
                      style={{ ...(c.num ? ui.tdNum : ui.td), textAlign: c.num ? 'center' : (c.ltr ? 'left' : 'right'), direction: c.ltr ? 'ltr' : 'rtl' }}>
                      {c.num ? fmtNum(v) : toFa(v)}
                    </td>
                  );
                })}
              </tr>
            );
          })}
        </tbody>
      </table>
      {rows.length === 0 && <div style={ui.empty}>{empty}</div>}
    </>
  );
}

/* ── پنجرهٔ مودال ── */
export function Modal({ title, onClose, width = 560, footer, children, z = 1000 }) {
  return (
    <div style={{ ...ui.overlay, zIndex: z }} onClick={onClose}>
      <div style={{ ...ui.modal, maxWidth: `${width}px` }} onClick={e => e.stopPropagation()}>
        <div style={ui.mHeader}>
          <div style={ui.mTitle}>{title}</div>
          <button onClick={onClose} style={ui.closeBtn}>✕</button>
        </div>
        <div style={ui.mBody}>{children}</div>
        {footer && <div style={ui.mFooter}>{footer}</div>}
      </div>
    </div>
  );
}

/* ── پیام ساده (فقط بستن) ── */
export function MsgBox({ lines, onClose, title = 'پیام' }) {
  if (!lines || !lines.length) return null;
  return <WarningDialog title={title} lines={lines} confirmLabel="تأیید" cancelLabel="بستن"
    onConfirm={onClose} onCancel={onClose} />;
}

/* ── تغییر موارد استفاده ──
   base: مسیر رکوردها (مثلاً '/goods')؛ رکورد انتخاب‌شده rec؛ canMove: مجوز دکمهٔ انتقال.
   پس از انتقال موفق onMoved فراخوانی می‌شود تا صفحه سؤال حذف رکورد فعلی را بپرسد. */
export function ChangeUsesDialog({ api = basicApi, base, rec, canMove, onClose, onMoved, onMessage }) {
  const [info, setInfo] = useState(null);
  const [thisYear, setThisYear] = useState(true);
  const [target, setTarget] = useState({ id: 0, display: '' });
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const cb = useRef({ onClose, onMessage });
  cb.current = { onClose, onMessage };
  useEffect(() => {
    let alive = true;
    api(`${base}/${rec.id}/uses/`).then(j => {
      if (!alive) return;
      if (j.ok) setInfo(j);
      else { cb.current.onMessage(j.msg || 'خطا'); cb.current.onClose(); }
    });
    return () => { alive = false; };
  }, [api, base, rec.id]);
  if (!info) return null;
  const move = async () => {
    setBusy(true); setErr('');
    const j = await api(`${base}/${rec.id}/move/`, { method: 'POST',
      body: JSON.stringify({ target_id: target.id, this_year: thisYear ? 1 : 0, year: info.year }) });
    setBusy(false);
    if (j.ok) onMoved(j); else setErr(j.msg || 'خطا');
  };
  return (
    <Modal title="تغییر موارد استفاده" onClose={onClose} width={560} z={1500}
      footer={<>
        <button onClick={onClose} style={ui.btnCancel}>بستن</button>
        {canMove && <button onClick={move} disabled={busy} style={ui.btnSave}>{busy ? '⏳' : 'انتقال'}</button>}
      </>}>
      {err && <div style={ui.errBox}>⚠️ {err}</div>}
      <div style={{ fontWeight: 700, color: '#1a2f50', marginBottom: '12px', lineHeight: 1.9 }}>{toFa(info.title)}</div>
      <div style={{ ...ui.row, alignItems: 'center' }}>
        <label style={{ display: 'flex', gap: '6px', alignItems: 'center', fontSize: '13px' }}>
          <input type="radio" checked={thisYear} onChange={() => setThisYear(true)} />سال {toFa(info.year)}
          <span style={{ color: '#777' }}>({toFa(info.cnt_year)})</span>
        </label>
        <label style={{ display: 'flex', gap: '6px', alignItems: 'center', fontSize: '13px' }}>
          <input type="radio" checked={!thisYear} onChange={() => setThisYear(false)} />کلیه اطلاعات
          <span style={{ color: '#777' }}>({toFa(info.cnt)})</span>
        </label>
      </div>
      <div style={{ fontSize: '12px', color: '#555', marginBottom: '8px' }}>تعداد موارد استفاده: {toFa(thisYear ? info.cnt_year : info.cnt)}</div>
      <div style={ui.row}>
        <Lookup label="به" api={api} text={target.display} endpoint={`${base}/targets/?exclude=${rec.id}`}
          onPick={r => setTarget({ id: r.id, display: r.display })} />
      </div>
    </Modal>
  );
}

/* ── سؤال حذف رکورد فعلی پس از انتقال ── */
export function AfterMoveAsk({ onYes, onNo }) {
  return <WarningDialog title="انتخاب کنید" lines={['تغییر انجام شد', 'آیا رکورد فعلی حذف شود؟']}
    confirmLabel="بلی" cancelLabel="خیر" onConfirm={onYes} onCancel={onNo} />;
}

export const btnDisabled = (st, on) => ({ ...st, opacity: on ? 1 : 0.4, cursor: on ? 'pointer' : 'default' });
